/**
 * viewportResize.js — Enterprise Viewport Resize & Collapse Engine
 * Mikhmel Cornerstone4D DICOM Viewer Pro v6
 *
 * EXPORTS (8):
 *   initViewportResize, destroyViewportResize,
 *   forceViewportRelayout, getCollapsedViewports,
 *   setCollapseThreshold, toggleViewportCollapse,
 *   getViewportResizeStats, setFABVisibility
 *
 * [ENTERPRISE UPGRADE]
 *  - Configurable collapse threshold
 *  - Manual collapse/restore per viewport
 *  - Force relayout API (triggers resize + re-observe)
 *  - FAB visibility control
 *  - Diagnostics: resize event counters, collapsed list
 *  - Proper cleanup / destroy
 *  - Debounced ResizeObserver (perf on rapid layout changes)
 *  - Viewport visibility IntersectionObserver for lazy render
 */

import { log } from '../utils/logger.js';



// [FIX] AbortController для очистки глобальных слушателей
let _ac = new AbortController();

// ══════════════════════════════════════════════════════════════
// STATE
// ══════════════════════════════════════════════════════════════

let _resizeObserver = null;
let _mutationObserver = null;
let _mobileMutationObserver = null;
let _intersectionObserver = null;
let _collapseThreshold = 50; // px — below this → collapsed
let _initialized = false;
let _fabVisible = true;

// Stats
let _stats = {
  resizeEvents: 0,
  collapseEvents: 0,
  expandEvents: 0,
  relayouts: 0,
  lastResizeAt: null,
};

/** @type {Set<string>} manually collapsed viewport IDs */
const _manuallyCollapsed = new Set();

// ══════════════════════════════════════════════════════════════
// 1. INIT
// ══════════════════════════════════════════════════════════════

export function initViewportResize() {
  // [FIX] Cleanup previous listeners
  if (_ac) _ac.abort();
  _ac = new AbortController();
  if (_initialized) return;
  _initialized = true;

  function init() {
    const vc = document.getElementById('viewport-container');
    if (!vc) return;

    // ── ResizeObserver for auto-collapse ──
    let _rafPending = false;
    const _pendingEntries = [];

    _resizeObserver = new ResizeObserver((entries) => {
      // Debounce via rAF to batch rapid resizes
      for (const entry of entries) _pendingEntries.push(entry);
      if (_rafPending) return;
      _rafPending = true;
      requestAnimationFrame(() => {
        _rafPending = false;
        _processResizeEntries(_pendingEntries.splice(0));
      });
    });

    function observeChildren() {
      _resizeObserver.disconnect();
      // [11.1.12] Следим ТОЛЬКО за окнами (id начинается с viewport-).
      // Прежний ':scope > div' ловил ЛЮБОЙ div-ребёнок контейнера, включая
      // полоску сравнения #cmp-align-bar. Её здоровая высота ~34px — всегда
      // ниже порога 50, поэтому ей вешался vp-collapsed, правило
      // index.html:727 прятало кнопки (display:none!important), полоска
      // сжималась до голых паддингов 20×12 и вырасти обратно не могла:
      // класс снимают только с выросших. Замок. Снаружи — «панели нет»,
      // хотя элемент жив и кнопок в нём четыре: поймано compareStatus +
      // getBoundingClientRect с экрана владельца, 20×12 при flex/visible.
      // Схлопывание задумано для ОКОН — окнами и ограничено, во всех
      // четырёх местах этого файла разом.
      const children = vc.querySelectorAll(':scope > div[id^="viewport-"]');
      for (const ch of children) _resizeObserver.observe(ch);
    }
    observeChildren();

    // Re-observe when children change
    _mutationObserver = new MutationObserver(observeChildren);
    _mutationObserver.observe(vc, { childList: true });

    // ── IntersectionObserver for visibility-based lazy render hints ──
    _intersectionObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        const el = entry.target;
        if (entry.isIntersecting) {
          el.classList.remove('vp-offscreen');
          el.classList.add('vp-visible');
        } else {
          el.classList.remove('vp-visible');
          el.classList.add('vp-offscreen');
        }
      }
    }, { root: vc, threshold: 0.1 });

    for (const ch of vc.querySelectorAll(':scope > div[id^="viewport-"]')) {
      _intersectionObserver.observe(ch);
    }

    // ── Mobile FAB hider ──
    if (window.innerWidth <= 768 || 'ontouchstart' in window) {
      _initMobileFABHider(vc);
    }

    log.debug('[ViewportResize] Initialized');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { signal: _ac.signal });
  } else {
    init();
  }
}

// ══════════════════════════════════════════════════════════════
// 2. COLLAPSE / EXPAND
// ══════════════════════════════════════════════════════════════

/**
 * Get list of currently collapsed viewport element IDs
 * @returns {string[]}
 */
export function getCollapsedViewports() {
  const collapsed = [];
  const vc = document.getElementById('viewport-container');
  if (!vc) return collapsed;
  for (const ch of vc.querySelectorAll(':scope > div[id^="viewport-"]')) {
    if (ch.classList.contains('vp-collapsed')) {
      collapsed.push(ch.id || '(unnamed)');
    }
  }
  return collapsed;
}

/**
 * Manually toggle collapse on a viewport by element ID
 * @param {string} vpElementId — e.g. 'viewport-axial'
 */
export function toggleViewportCollapse(vpElementId) {
  const el = document.getElementById(vpElementId);
  if (!el) { log.warn(`[ViewportResize] Element ${vpElementId} not found`); return; }

  if (_manuallyCollapsed.has(vpElementId)) {
    // Restore
    _manuallyCollapsed.delete(vpElementId);
    el.classList.remove('vp-collapsed');
    el.style.removeProperty('width');
    el.style.removeProperty('height');
    _stats.expandEvents++;
  } else {
    // Collapse
    _manuallyCollapsed.add(vpElementId);
    el.classList.add('vp-collapsed');
    _stats.collapseEvents++;
  }
  window.dispatchEvent(new Event('resize'));
}

/**
 * Set collapse threshold (px). Viewports smaller than this auto-collapse.
 * @param {number} px
 */
export function setCollapseThreshold(px) {
  _collapseThreshold = Math.max(10, Math.min(px, 200));
  // Re-evaluate existing
  forceViewportRelayout();
  log.debug(`[ViewportResize] Collapse threshold = ${_collapseThreshold}px`);
}

// ══════════════════════════════════════════════════════════════
// 3. RELAYOUT
// ══════════════════════════════════════════════════════════════

/**
 * Force full relayout: disconnect+reconnect observers, dispatch resize
 */
export function forceViewportRelayout() {
  const vc = document.getElementById('viewport-container');
  if (!vc) return;

  _stats.relayouts++;

  // Re-evaluate all children
  if (_resizeObserver) {
    _resizeObserver.disconnect();
    for (const ch of vc.querySelectorAll(':scope > div[id^="viewport-"]')) {
      _resizeObserver.observe(ch);
      // Manual check
      const w = ch.offsetWidth;
      const h = ch.offsetHeight;
      if (w < _collapseThreshold || h < _collapseThreshold) {
        ch.classList.add('vp-collapsed');
      } else if (!_manuallyCollapsed.has(ch.id)) {
        ch.classList.remove('vp-collapsed');
      }
    }
  }

  window.dispatchEvent(new Event('resize'));
  log.debug('[ViewportResize] Force relayout');
}

// ══════════════════════════════════════════════════════════════
// 4. FAB CONTROL
// ══════════════════════════════════════════════════════════════

/**
 * Show/hide FAB buttons inside viewport on mobile
 * @param {boolean} visible
 */
export function setFABVisibility(visible) {
  _fabVisible = visible;
  const vc = document.getElementById('viewport-container');
  if (!vc) return;
  const fabs = vc.querySelectorAll('button:not(.vp-zoom-round):not(.vp-zoom-btn)');
  for (const b of fabs) {
    if (b.closest('.vp-zoom-spread') || b.closest('.vp-zoom-controls') ||
        b.closest('#mobile-nav') || b.closest('.mobile-drawer') ||
        b.closest('.topbar') || b.closest('#mobile-topbar')) continue;
    const parent = b.parentElement;
    if (parent?.id?.startsWith('viewport-')) {
      b.style.setProperty('display', visible ? '' : 'none', 'important');
    }
  }
  log.debug(`[ViewportResize] FAB visibility = ${visible}`);
}

// ══════════════════════════════════════════════════════════════
// 5. DIAGNOSTICS
// ══════════════════════════════════════════════════════════════

/**
 * Get resize statistics
 * @returns {Object}
 */
export function getViewportResizeStats() {
  return {
    ..._stats,
    collapseThreshold: _collapseThreshold,
    collapsedViewports: getCollapsedViewports(),
    manuallyCollapsed: [..._manuallyCollapsed],
    fabVisible: _fabVisible,
    observerActive: !!_resizeObserver,
    intersectionActive: !!_intersectionObserver,
  };
}

// ══════════════════════════════════════════════════════════════
// 6. CLEANUP
// ══════════════════════════════════════════════════════════════

export function destroyViewportResize() {
  if (_resizeObserver) { _resizeObserver.disconnect(); _resizeObserver = null; }
  if (_mutationObserver) { _mutationObserver.disconnect(); _mutationObserver = null; }
  if (_mobileMutationObserver) { _mobileMutationObserver.disconnect(); _mobileMutationObserver = null; }
  if (_intersectionObserver) { _intersectionObserver.disconnect(); _intersectionObserver = null; }
  _manuallyCollapsed.clear();
  _initialized = false;
  log.debug('[ViewportResize] Destroyed');
}

// ══════════════════════════════════════════════════════════════
// INTERNAL
// ══════════════════════════════════════════════════════════════

function _processResizeEntries(entries) {
  for (const entry of entries) {
    const el = entry.target;
    const w = entry.contentRect.width;
    const h = entry.contentRect.height;
    _stats.resizeEvents++;
    _stats.lastResizeAt = Date.now();

    if (_manuallyCollapsed.has(el.id)) continue; // don't auto-restore manually collapsed

    if (w < _collapseThreshold || h < _collapseThreshold) {
      if (!el.classList.contains('vp-collapsed')) {
        el.classList.add('vp-collapsed');
        _stats.collapseEvents++;
      }
    } else {
      if (el.classList.contains('vp-collapsed')) {
        el.classList.remove('vp-collapsed');
        _stats.expandEvents++;
      }
    }
  }
}

function _initMobileFABHider(vc) {
  function hideNonZoomButtons(root) {
    if (!root?.querySelectorAll) return;
    if (!_fabVisible) return; // respect global setting
    const btns = root.querySelectorAll('button, [role="button"]');
    for (const b of btns) {
      if (b.classList.contains('vp-zoom-round') || b.classList.contains('vp-zoom-btn')) continue;
      if (b.closest('.vp-zoom-spread') || b.closest('.vp-zoom-controls')) continue;
      if (b.closest('#mobile-nav') || b.closest('.mobile-drawer') ||
          b.closest('.topbar') || b.closest('#mobile-topbar')) continue;
      const parent = b.parentElement;
      if (parent?.id?.startsWith('viewport-')) {
        b.style.setProperty('display', 'none', 'important');
      }
    }
  }

  _mobileMutationObserver = new MutationObserver((muts) => {
    for (const m of muts) {
      for (const added of m.addedNodes) {
        if (added.nodeType === 1) hideNonZoomButtons(added.parentElement || vc);
      }
    }
  });
  _mobileMutationObserver.observe(vc, { childList: true, subtree: true });
  hideNonZoomButtons(vc);
}
